// Jupnet as a source. The api builds the open and signs it as fee payer and the program's
// `opener` at `/submit`, so the user adds one ed25519 signature over the message and needs no gas
// token. A Jupnet transaction is `shortvec(n)`, then each signature typed (`shortvec(65) ‖ 0x00 ‖
// sig64`, 0x00 = Ed25519), then a legacy message byte-for-byte Solana's. The user's Jupnet key
// (`jupnet-key.ts`) signs the message and its signature goes into the transaction's slot
// (services/crates/gi-chains/src/jupnet_intent.rs `with_signature`).

import { Keypair, Message, PublicKey, Transaction, VersionedTransaction } from "@solana/web3.js";
import type { Family } from "./chains";
import { fromBase58 } from "./jupnet-key";
import { normalizeSuiCoin } from "./sui";
import { base58, nativeTokenAddr } from "./wire";

/** `/build`'s `tx` for a Jupnet source. */
export type JupnetBuilt = { vm: "jupnet"; tx_base64: string; message_base64: string; signers: string[]; user_signature_index: number; recent_blockhash: string };

const ED25519 = 0;
const SIG_LEN = 64;

export const fromB64 = (s: string) => Uint8Array.from(atob(s), (c) => c.charCodeAt(0));
export const toB64 = (b: Uint8Array) => btoa(Array.from(b, (x) => String.fromCharCode(x)).join(""));
const hex = (b: Uint8Array) => Array.from(b, (x) => x.toString(16).padStart(2, "0")).join("");
const same = (a: Uint8Array, b: Uint8Array) => a.length === b.length && a.every((x, i) => x === b[i]);

/** Solana's compact-u16: 7 bits a byte, low first, at most 3 bytes. */
export function shortvec(n: number): number[] {
  if (!Number.isInteger(n) || n < 0 || n > 0xffff) throw new Error(`shortvec: ${n} out of range`);
  const out: number[] = [];
  while (n > 0x7f) {
    out.push((n & 0x7f) | 0x80);
    n >>= 7;
  }
  out.push(n);
  return out;
}

/** The shortvec at `at`: its value and the offset past it. */
export function readShortvec(b: Uint8Array, at: number): [number, number] {
  let n = 0;
  for (const shift of [0, 7, 14]) {
    const byte = b[at++];
    if (byte === undefined) throw new Error("shortvec past the end");
    n |= (byte & 0x7f) << shift;
    if (!(byte & 0x80)) return [n, at];
  }
  throw new Error("shortvec too long");
}

/** A Jupnet transaction → its Ed25519 signatures, where each one's 64 bytes start, and the message. */
export function decodeJupnetTx(tx: Uint8Array): { signatures: Uint8Array[]; offsets: number[]; message: Uint8Array } {
  let [n, at] = readShortvec(tx, 0);
  const signatures: Uint8Array[] = [];
  const offsets: number[] = [];
  for (let i = 0; i < n; i++) {
    const [len, next] = readShortvec(tx, at);
    if (len !== 1 + SIG_LEN || tx[next] !== ED25519 || next + len > tx.length) throw new Error(`signature ${i} is not an Ed25519 signature`);
    offsets.push(next + 1);
    signatures.push(tx.slice(next + 1, next + len));
    at = next + len;
  }
  return { signatures, offsets, message: tx.slice(at) };
}

/** Jupnet transaction bytes from signatures (all Ed25519) and the message. */
export function encodeJupnetTx(signatures: Uint8Array[], message: Uint8Array): Uint8Array {
  const out = [...shortvec(signatures.length)];
  for (const s of signatures) {
    if (s.length !== SIG_LEN) throw new Error("an Ed25519 signature is 64 bytes");
    out.push(...shortvec(1 + SIG_LEN), ED25519, ...s);
  }
  return Uint8Array.from([...out, ...message]);
}

/** `tx` with `signature` in slot `index`, every other byte as it was. */
export function withSignature(tx: Uint8Array, index: number, signature: Uint8Array): Uint8Array {
  if (signature.length !== SIG_LEN) throw new Error("an Ed25519 signature is 64 bytes");
  const { offsets } = decodeJupnetTx(tx);
  const at = offsets[index];
  if (at === undefined) throw new Error(`signature index ${index} of ${offsets.length}`);
  const out = tx.slice();
  out.set(signature, at);
  return out;
}

/** gum-intent's Jupnet program (contracts/jupnet `declare_id!`): the only program a Jupnet open
 *  may call besides the compute budget. The demo's own anchor, never the api's word. */
export const GUM_JUPNET_PROGRAM = "SoLJfGmmuZZSJzuwcqRHgi547b2t8t1xn3UZsXkaFWn";
const COMPUTE_BUDGET = "ComputeBudget111111111111111111111111111111";
const JPL_TOKEN_2022 = new PublicKey("Tokenis9xgQh7yMRbNBnV6uFq7LANbuZJwebxWBWixf");
const ATA_PROGRAM = new PublicKey("ATokenGPvbdGVxr1b2hvZbsiqW5xWH25efTNsLJA8knL");
/** `sha256("global:open_intent")[..8]`, as in the committed IDL. */
const OPEN_INTENT = [83, 191, 214, 191, 204, 246, 15, 151];
/** `sha256("global:open_intent_basket")[..8]`, as in the committed IDL. */
const OPEN_INTENT_BASKET = [67, 252, 24, 89, 193, 241, 99, 191];

/** What the user asked for, to hold the api's Jupnet open against before the user's key signs it. */
export type ExpectedOpen = {
  program: string;
  user: string;
  inputMint: string;
  /** the gross, smallest units: what the open transfers (never 0, which takes the whole balance) */
  amount: string;
  minGross: string;
  maxGross: string;
  dstChainId: number;
  /** the destination token as the user chose it (chain-native), and the destination's family */
  dstToken: string;
  dstFamily: Family;
  /** the recipient as 32 bytes (`recipientRaw`) */
  dstRecipient: Uint8Array;
  delivery: number;
  rateNum: string;
  rateDen: string;
  slippageBps: number;
  validitySeconds: number;
  builderFeeBps: number;
  protocolFeeBps: number;
  /** a USD basket (`open_intent_basket`): each stablecoin and what the open transfers of it, in
   *  order; `inputMint` / `amount` are then unused and `minGross` / `maxGross` are 6-decimal USD */
  inputs?: { mint: string; amount: string }[];
  /** the exclusive fill window the confirmed quote asked for (`exclusive_ms`; absent or 0: none,
   *  the open must carry a zero solver and 0 seconds). Bounds what the open may sign, never taken
   *  from the `/build` reply being checked. The solver's address there is not checked: the quote
   *  names the solver, not its fill address on the destination */
  exclusiveMs?: number;
};

/** A recipient address as the wire's 32 bytes: EVM left-padded, base58 (Solana, Jupnet), Sui hex.
 *  Trimmed first, as the form's validation trims it: an address it accepts always converts. */
export function recipientRaw(family: Family | "jupnet", address: string): Uint8Array {
  const addr = address.trim();
  if (family === "evm" || family === "sui") {
    const h = addr.replace(/^0x/, "").padStart(64, "0");
    return Uint8Array.from(h.match(/../g)!.map((b) => parseInt(b, 16)));
  }
  return new PublicKey(addr).toBytes();
}

const le = (b: Uint8Array) => b.reduceRight((n, x) => (n << 8n) | BigInt(x), 0n);

/**
 * Refuse a Jupnet open that is not exactly what the user asked for: the api builds and co-signs
 * it, and the user's key signs whatever bytes it is given (no wallet shows them), so a wrong or
 * compromised api must not be able to have it sign anything else — another program, a whole-
 * balance transfer, another recipient or token, other terms.
 */
export function checkJupnetOpen(message: Uint8Array, e: ExpectedOpen): void {
  const refuse = (why: string) => {
    throw new Error(`the api's Jupnet open is not the one you asked for (${why}); nothing was signed`);
  };
  const msg = Message.from(message);
  const key = (i: number) => msg.accountKeys[i]!;
  const opens = msg.instructions.filter((ix) => {
    const program = key(ix.programIdIndex).toBase58();
    if (program === COMPUTE_BUDGET) {
      const data = bs58Bytes(ix.data);
      if (ix.accounts.length || ![2, 3].includes(data[0]!)) refuse("an unexpected compute-budget instruction");
      return false;
    }
    if (program !== e.program) refuse(`a call to ${program}`);
    return true;
  });
  if (opens.length !== 1) refuse(`${opens.length} calls to the program`);
  const ix = opens[0]!;
  const data = bs58Bytes(ix.data);
  const basket = e.inputs;
  if ((basket ? OPEN_INTENT_BASKET : OPEN_INTENT).some((b, i) => data[i] !== b)) refuse(basket ? "not open_intent_basket" : "not open_intent");
  const acc = ix.accounts.map(key);
  const user = new PublicKey(e.user);
  const ataOf = (mint: PublicKey) => PublicKey.findProgramAddressSync([user.toBuffer(), JPL_TOKEN_2022.toBuffer(), mint.toBuffer()], ATA_PROGRAM)[0];
  if (basket) {
    // the fixed accounts, then (mint, the user's account, the escrow's) per stablecoin, in order
    if (acc.length !== 11 + 3 * basket.length || !acc[1]!.equals(user) || !acc[8]!.equals(JPL_TOKEN_2022)) refuse("its accounts");
    basket.forEach((c, i) => {
      const mint = new PublicKey(c.mint);
      if (!acc[11 + 3 * i]!.equals(mint) || !acc[12 + 3 * i]!.equals(ataOf(mint))) refuse(`its accounts for ${c.mint}`);
    });
  } else {
    const mint = new PublicKey(e.inputMint);
    if (acc.length !== 14 || !acc[1]!.equals(user) || !acc[8]!.equals(mint) || !acc[9]!.equals(ataOf(mint)) || !acc[11]!.equals(JPL_TOKEN_2022)) refuse("its accounts");
  }

  let at = 8;
  const take = (n: number) => {
    if (at + n > data.length) refuse("its arguments are short");
    const b = data.slice(at, at + n);
    at += n;
    return b;
  };
  const u = (n: number) => le(take(n));
  const want = (what: string, got: bigint | string | number, wanted: bigint | string | number) => {
    if (BigInt(got) !== BigInt(wanted)) refuse(`${what} ${got}, not ${wanted}`);
  };
  want("destination chain", u(8), e.dstChainId);
  take(32); // dst_endpoint: the program requires a registered, outbound peer for the chain
  if (basket) {
    want("stablecoins", u(4), basket.length);
    for (const c of basket) {
      const amount = u(32);
      if (amount === 0n) refuse("a transfer of the whole balance");
      want(`amount of ${c.mint}`, amount, c.amount);
    }
  } else {
    const amount = u(32);
    if (amount === 0n) refuse("a transfer of the whole balance");
    want("amount", amount, e.amount);
  }
  want("min_gross", u(32), e.minGross);
  want("max_gross", u(32), e.maxGross);
  want("rate_num", u(16), e.rateNum);
  want("rate_den", u(16), e.rateDen);
  want("slippage_bps", u(2), e.slippageBps);
  const tokenLen = Number(u(4));
  const token = nativeTokenAddr(`0x${hex(take(tokenLen))}`, e.dstFamily);
  // compared as each family writes one token: EVM in any case, a Sui coin type with its address in
  // full (the wire decodes `0x2::sui::SUI` padded), a mint as it is
  const sameToken =
    e.dstFamily === "evm"
      ? token.toLowerCase() === e.dstToken.toLowerCase()
      : e.dstFamily === "sui"
        ? normalizeSuiCoin(token) === normalizeSuiCoin(e.dstToken)
        : token === e.dstToken;
  if (!sameToken) refuse(`destination token ${token}`);
  if (!same(take(32), e.dstRecipient)) refuse("another recipient");
  want("validity", u(8), e.validitySeconds);
  if (take(1)[0] !== 0) refuse("a builder the demo did not name");
  want("builder_fee_bps", u(2), e.builderFeeBps);
  want("protocol_fee_bps", u(2), e.protocolFeeBps);
  want("delivery", u(1), e.delivery);
  // wire v5: the signed exclusive fill window, bounded by the confirmed quote (none: zero) — at
  // most what it asked for, rounded up to whole seconds, and half the validity (the api's caps)
  const exSolver = take(32);
  const exSeconds = u(8);
  if (exSolver.every((b) => b === 0) !== (exSeconds === 0n)) refuse("a non-canonical exclusive window");
  if (exSeconds !== 0n) {
    const asked = Math.ceil((e.exclusiveMs ?? 0) / 1000);
    if (asked === 0) refuse(`an exclusive window (solver ${hex(exSolver)}) the quote did not ask for`);
    if (exSeconds > BigInt(asked)) refuse(`an exclusive window of ${exSeconds}s, longer than the quote's ${asked}s`);
    if (exSeconds > BigInt(Math.floor(e.validitySeconds / 2))) refuse("an exclusive window over half the validity");
  }
  if (at !== data.length) refuse("trailing argument bytes");
}

const bs58Bytes = (s: string) => fromBase58(s);

/** A Jupnet open to sign: the api's transaction, its message, and the user's slot. */
export type JupnetSigning = { bytes: Uint8Array; message: Uint8Array; index: number; user: PublicKey };

export function jupnetSigning(built: JupnetBuilt, user: string): JupnetSigning {
  const bytes = fromB64(built.tx_base64);
  const { signatures, message } = decodeJupnetTx(bytes);
  // the two forms the api returns must be the same message
  if (!same(message, fromB64(built.message_base64))) throw new Error("the api's Jupnet transaction and message differ; nothing was signed");
  const msg = Message.from(message);
  const userKey = new PublicKey(user);
  const index = built.user_signature_index;
  if (msg.header.numRequiredSignatures !== signatures.length) throw new Error(`the Jupnet message needs ${msg.header.numRequiredSignatures} signatures, the transaction has ${signatures.length}`);
  if (!msg.accountKeys[index]?.equals(userKey) || built.signers[index] !== user) throw new Error(`the api built this Jupnet open for another signer than ${user}; nothing was signed`);
  // the api pays every open's fee: the user's key as the fee payer (account 0) would pay it, and
  // any priority fee the api set, from the user's JUP
  if (index === 0 || msg.accountKeys[0]!.equals(userKey)) throw new Error("the api made your Jupnet key this open's fee payer; nothing was signed");
  return { bytes, message, index, user: userKey };
}

/**
 * The signed Jupnet transaction, base64 (what `/submit` takes): `key` (the user's Jupnet key)
 * signs the api's message bytes as they are — nothing recompiles them — and every signature
 * present must verify over them before anything is sent. The api's slot is empty: it signs as fee
 * payer and `opener` at `/submit`, after checking the user's signature.
 */
export function signJupnetOpen(s: JupnetSigning, key: Keypair): string {
  if (!key.publicKey.equals(s.user)) throw new Error(`this open is for ${s.user.toBase58()}, not your Jupnet key ${key.publicKey.toBase58()}`);
  const msg = Message.from(s.message);
  if (!same(msg.serialize(), s.message)) throw new Error("the Jupnet message does not round-trip; nothing was signed");
  const vtx = new VersionedTransaction(msg);
  vtx.sign([key]);
  const out = withSignature(s.bytes, s.index, vtx.signatures[s.index]!);
  const check = decodeJupnetTx(out);
  // an all-zero slot reads as missing (the api's, until /submit); the user's is never missing
  if (check.signatures[s.index]!.every((b) => b === 0) || !Transaction.populate(msg, check.signatures.map((x) => base58(hex(x)))).verifySignatures(false)) {
    throw new Error("a signature does not verify over the Jupnet open; nothing was sent");
  }
  return toB64(out);
}

/** A JPL Token-2022 mint's decimals: mint_authority COption (36), supply U256 (32), then decimals. */
export const JPL_MINT_DECIMALS_OFFSET = 68;

/** Decimals of Jupnet mints over a (public) Jupnet RPC; a mint not read is absent. */
export async function jupnetDecimals(rpc: string, mints: string[], f: typeof fetch = fetch): Promise<Record<string, number>> {
  if (!mints.length) return {};
  const r = await f(rpc, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ jsonrpc: "2.0", id: 1, method: "getMultipleAccounts", params: [mints, { encoding: "base64", dataSlice: { offset: JPL_MINT_DECIMALS_OFFSET, length: 1 } }] }),
  });
  const j = (await r.json()) as any;
  const out: Record<string, number> = {};
  (j?.result?.value ?? []).forEach((v: any, i: number) => {
    const d = v?.data?.[0] ? fromB64(v.data[0])[0] : undefined;
    if (d !== undefined) out[mints[i]!] = d;
  });
  return out;
}
